import type { TicketListPayload } from "@noverik/contracts";
import { ChevronLeft, ChevronRight, Filter, Plus, Search } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiRequest, ApiRequestError } from "../lib/api";

type Props = { mode?: "client" | "agent" };

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
}

export function TicketsPage({ mode = "client" }: Props) {
  const base = mode === "agent" ? "/desk/chamados" : "/chamados";
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const state = searchParams.get("state") ?? "";
  const status = searchParams.get("status")?.trim() ?? "";
  const parsedPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const [data, setData] = useState<TicketListPayload | null>(null);
  const [input, setInput] = useState(query);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      page: String(page),
      limit: "25",
    });
    if (query) params.set("q", query);
    if (state) params.set("state", state);
    if (status) params.set("status", status);

    try {
      const next = await apiRequest<TicketListPayload>("/tickets?" + params.toString());
      setData(next);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível carregar os chamados.",
      );
    } finally {
      setLoading(false);
    }
  }, [page, query, state, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateParams = (changes: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }

    setSearchParams(next);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    updateParams({ q: input.trim(), page: "" });
  };

  const changeState = (next: string) => {
    updateParams({ state: next, status: "", page: "" });
  };

  const showPendingClient = () => {
    updateParams({ state: "", status: "Pendente cliente", page: "" });
  };

  const changePage = (next: number) => {
    updateParams({ page: next > 1 ? String(next) : "" });
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">{mode === "agent" ? "OPERAÇÃO" : "ATENDIMENTO"}</span>
          <h1>{mode === "agent" ? "Fila de chamados" : "Meus chamados"}</h1>
          <p>
            {data
              ? data.total + " chamado" + (data.total === 1 ? "" : "s") + " encontrado" + (data.total === 1 ? "" : "s") + "."
              : "Acompanhe o status, a prioridade e as últimas atualizações."}
          </p>
        </div>
        <Link className="button primary" to={mode === "agent" ? "/desk/novo" : "/novo"}>
          <Plus size={17} /> Novo chamado
        </Link>
      </div>

      <div className="ticket-controls">
        <form className="searchbox inline" onSubmit={submitSearch}>
          <Search size={17} />
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Buscar por número, assunto ou conteúdo..."
          />
          <button type="submit" className="search-submit">Buscar</button>
        </form>

        <div className="state-filter" aria-label="Filtrar status">
          <Filter size={16} />
          {[
            ["", "Todos"],
            ["open", "Abertos"],
            ["closed", "Fechados"],
          ].map(([value, label]) => (
            <button
              key={value || "all"}
              className={state === value && status === "" ? "active" : ""}
              onClick={() => changeState(value)}
            >
              {label}
            </button>
          ))}
          {mode === "agent" && (
            <button
              className={status === "Pendente cliente" ? "active" : ""}
              onClick={showPendingClient}
            >
              Pendente cliente
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="data-message error">
          <strong>Não foi possível carregar.</strong>
          <span>{error}</span>
          <button className="button secondary" onClick={() => void load()}>Tentar novamente</button>
        </div>
      )}

      {!error && (
        <div className="ticket-table-wrap">
          <table className="ticket-table">
            <thead>
              <tr>
                <th>Chamado</th>
                <th>Assunto</th>
                {mode === "agent" && <th>Solicitante</th>}
                <th>Status</th>
                <th>Prioridade</th>
                <th>Departamento</th>
                <th>Atualização</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={mode === "agent" ? 7 : 6}>
                    <div className="table-loading">Carregando chamados...</div>
                  </td>
                </tr>
              )}

              {!loading && data?.items.length === 0 && (
                <tr>
                  <td colSpan={mode === "agent" ? 7 : 6}>
                    <div className="table-empty">
                      <strong>Nenhum chamado encontrado.</strong>
                      <span>Ajuste os filtros ou abra uma nova solicitação.</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && data?.items.map((ticket) => (
                <tr key={ticket.id}>
                  <td>
                    <Link className="ticket-number" to={base + "/" + ticket.number}>
                      #{ticket.number}
                    </Link>
                  </td>
                  <td>
                    <Link className="ticket-subject" to={base + "/" + ticket.number}>
                      {ticket.subject || "Sem assunto"}
                    </Link>
                  </td>
                  {mode === "agent" && (
                    <td>
                      <span className="requester-name">{ticket.requester.name || "—"}</span>
                      <small>{ticket.requester.email}</small>
                    </td>
                  )}
                  <td>
                    <span className={"status status-" + ticket.status.state}>{ticket.status.label}</span>
                    {ticket.overdue && (
                      <span className="status status-overdue" title="Prazo SLA excedido">Atrasado</span>
                    )}
                  </td>
                  <td>{ticket.priority || "—"}</td>
                  <td>{ticket.department || "—"}</td>
                  <td>{formatDate(ticket.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && data.pages > 1 && (
        <div className="pagination">
          <span>Página {data.page} de {data.pages}</span>
          <div>
            <button
              className="button secondary"
              disabled={data.page <= 1}
              onClick={() => changePage(Math.max(1, page - 1))}
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <button
              className="button secondary"
              disabled={data.page >= data.pages}
              onClick={() => changePage(page + 1)}
            >
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
